<template>
    <div
        v-if="healthSurveys"
        class="container-fluid adminGroupHealthSurveysWrapper">
        <div class="d-block d-md-flex justify-content-between mb-3">
            <div class="d-block d-md-flex flex-wrap align-items-center">
                <div class="d-block d-md-flex align-items-center mr-2">
                    <div class="mt-2 mb-2 ml-md-2 mb-md-0">
                        <span class="small">開始日</span>
                    </div>
                    <div class="mt-2 mb-2 ml-md-2 mb-md-0">
                        <div class="input-group input-group-sm">
                            <input
                                v-model="search.survey_start_date_gte"
                                @change="onChangeSearchSurveyStartDateGte"
                                type="date"
                                class="form-control"
                                :class="{ 'is-invalid' : errors.survey_start_date_gte }"
                                style="min-width: 100px;">
                        </div>
                    </div>
                    <span class="small mt-2 ml-2">〜</span>
                    <div class="mt-2 mb-2 ml-md-2 mb-md-0">
                        <div class="input-group input-group-sm">
                            <input
                                v-model="search.survey_start_date_lte"
                                @change="onChangeSearchSurveyStartDateLte"
                                type="date"
                                class="form-control"
                                :class="{ 'is-invalid' : errors.survey_start_date_lte }"
                                style="min-width: 100px;">
                        </div>
                    </div>
                </div>
                <div class="d-block d-md-flex align-items-center mr-2">
                    <div class="mt-2 mb-2 ml-md-2 mb-md-0">
                        <span class="small">終了日</span>
                    </div>
                    <div class="mt-2 mb-2 ml-md-2 mb-md-0">
                        <div class="input-group input-group-sm">
                            <input
                                v-model="search.survey_finish_date_gte"
                                @change="onChangeSearchSurveyFinishDateGte"
                                type="date"
                                class="form-control"
                                :class="{ 'is-invalid' : errors.survey_finish_date_gte }"
                                style="min-width: 100px;">
                        </div>
                    </div>
                    <span class="small mt-2 ml-2">〜</span>
                    <div class="mt-2 mb-2 ml-md-2 mb-md-0">
                        <div class="input-group input-group-sm">
                            <input
                                v-model="search.survey_finish_date_lte"
                                @change="onChangeSearchSurveyFinishDateLte"
                                type="date"
                                class="form-control"
                                :class="{ 'is-invalid' : errors.survey_finish_date_lte }"
                                style="min-width: 100px;">
                        </div>
                    </div>
                </div>
                <div class="d-block d-md-flex align-items-center mr-2">
                    <div class="mt-2 mb-2 ml-md-2 mb-md-0">
                        <span class="small">質問デッキ</span>
                    </div>
                    <div class="mt-2 mb-2 ml-md-2 mb-md-0">
                        <div class="input-group input-group-sm">
                            <select
                                v-model="search.health_survey_template_id"
                                @change="onChangeSearchHealthSurveyTemplateId"
                                class="form-control"
                                :class="{ 'is-invalid' : errors.health_survey_template_id }">
                                <option
                                    key="health_survey_template_id"
                                    :value="null">
                                    選択してください
                                </option>
                                <option
                                    v-for="(template, index) in healthSurveyTemplates"
                                    :key="`health_survey_template_id_${index}`"
                                    :value="template.id">
                                    {{ template.name }}
                                </option>
                            </select>
                        </div>
                    </div>
                </div>
                <div class="d-block d-md-flex align-items-center mr-2">
                    <div class="mt-2 mb-2 ml-md-2 mb-md-0">
                        <span class="small">ステータス</span>
                    </div>
                    <div class="mt-2 mb-2 ml-md-2 mb-md-0">
                        <div class="input-group input-group-sm">
                            <select
                                v-model="search.status"
                                @change="onChangeSearchStatus"
                                class="form-control">
                                <option :value="null">選択してください</option>
                                <option
                                    v-for="status in healthSurveyStatus.all"
                                    :value="status.value">
                                    {{ status.text }}
                                </option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>
            <div class="d-flex align-items-center mt-3">
                <div>
                    <b-button
                        variant="yokumiru-orange"
                        @click="onClickDownloadTerm"
                        :disabled="inProgress">
                        選択期間中の集計データをダウンロード
                    </b-button>
                </div>
            </div>
        </div>

        <div class="row justify-content-center">
            <div class="col-md-12">
                <b-table
                    striped
                    hover
                    :items="healthSurveys"
                    :fields="fields"
                    :sort-by.sync="morphState.sortBy"
                    :sort-desc.sync="morphState.sortDesc"
                    @sort-changed="fetchHealthSurveys"
                    :responsive="true"
                    class="table-bordered m-table">
                    <template v-slot:cell(health_survey_template_id)="data">
                        {{ data.item.health_survey_template.name }}
                    </template>

                    <template v-slot:cell(member_count)="data">
                        {{ `${data.item.member_count}人` }}
                    </template>

                    <template v-slot:cell(answer_member_count)="data">
                        {{ `${data.item.answer_member_count}人` }}
                    </template>

                    <template v-slot:cell(survey_start_date)="data">
                        {{ helper.dateForHuman(data.item.survey_start_date, 'Asia/Tokyo') }}
                    </template>

                    <template v-slot:cell(survey_finish_date)="data">
                        {{ helper.dateForHuman(data.item.survey_finish_date, 'Asia/Tokyo') }}
                    </template>

                    <template v-slot:cell(status)="data">
                        <div v-if="data.item.status === healthSurveyStatus.active">
                            <div class="d-flex align-items-center">
                                <div class="text-success mr-3">受付中　</div>
                                <b-button
                                    type="button"
                                    class="btn btn-sm"
                                    variant="yokumiru-dark"
                                    @click="onClickPause(data.item)">
                                    一時停止
                                </b-button>
                            </div>
                        </div>
                        <div v-if="data.item.status === healthSurveyStatus.paused">
                            <div class="d-flex align-items-center">
                                <div class="text-muted mr-3">一時停止</div>
                                <b-button
                                    type="button"
                                    class="btn btn-sm"
                                    variant="success"
                                    @click="onClickResume(data.item)">
                                    受付再開
                                </b-button>
                            </div>
                        </div>
                        <div v-if="data.item.status === healthSurveyStatus.finished" class="text-danger">終了済</div>
                    </template>

                    <template v-slot:cell(actions)="data">
                        <div class="d-flex justify-content-end align-items-center">
                            <b-button
                                type="button"
                                class="btn btn-sm"
                                variant="yokumiru-orange"
                                @click="onClickDownloadDaily(data.item)">
                                データをダウンロード
                            </b-button>
                        </div>
                    </template>
                </b-table>

                <div class="d-flex flex-wrap justify-content-between align-items-center">
                    <b-pagination
                        :total-rows="morphState.total"
                        :per-page="morphState.per_page"
                        v-model="morphState.current_page"
                        @change="fetchHealthSurveys">
                    </b-pagination>

                    <div class="d-flex flex-wrap align-items-center">
                        <div class="mr-3 mb-3">
                            <span class="small">{{ dispPaginationDetail }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <admin-health-survey-pause-modal/>
        <admin-health-survey-resume-modal/>
    </div>
</template>

<script>
import { mapMutations } from 'vuex'
import constants from '../../../constants'
import YokumiruHelper from '../../../YokumiruHelper'
import moment from 'moment-timezone'

export default {
    name: 'AdminGroupHealthSurveys',

    props: {
        group: {
            type: Object,
            required: true,
        },
    },

    data: function () {
        return {
            helper: YokumiruHelper,
            search: {
                survey_start_date_gte: null,
                survey_start_date_lte: null,
                survey_finish_date_gte: null,
                survey_finish_date_lte: null,
                health_survey_template_id: null,
                status: null,
            },
            healthSurveyTemplates: [],
            healthSurveys: null,
            healthSurveyStatus: constants.healthSurveys,
            inProgress: false,
            errors: {},
            fields: [
                {
                    key: 'id',
                    label: '#',
                    sortable: true,
                },
                {
                    key: 'health_survey_template_id',
                    label: '質問デッキ',
                    sortable: true,
                },
                {
                    key: 'member_count',
                    label: '対象者数',
                    sortable: true,
                },
                {
                    key: 'answer_member_count',
                    label: '有効回答者数',
                    sortable: true,
                },
                {
                    key: 'survey_start_date',
                    label: '開始日',
                    sortable: true,
                },
                {
                    key: 'survey_finish_date',
                    label: '終了日',
                    sortable: true,
                },
                {
                    key: 'status',
                    label: 'ステータス',
                    sortable: true,
                },
                {
                    key: 'actions',
                    label: '',
                    class: 'text-center',
                    sortable: false,
                },
            ],
        }
    },

    computed: {
        isCompany: function () {
            return this.helper.isCompany(this.group)
        },

        isOrganization: function () {
            return this.helper.isOrganization(this.group)
        },

        morphState: function () {
            if (this.isCompany) {
                return this.$store.state.adminCompanyHealthSurveys
            }
            else if (this.isOrganization) {
                return this.$store.state.adminOrganizationHealthSurveys
            }
            return null
        },

        /**
         * ページング詳細表示
         * @returns {string}
         */
        dispPaginationDetail: function () {
            if (this.morphState.total < 1) {
                return ''
            }
            return `全${this.morphState.total}件中 ${this.morphState.from}～${this.morphState.to}件表示`
        },
    },

    created: function () {
        this.initQueryFromState()
        this.initSearchCondition()
        this.fetchHealthSurveyTemplates()
        this.fetchHealthSurveys()
        bus.$on('health-survey-paused', this.fetchHealthSurveys)
        bus.$on('health-survey-resumed', this.fetchHealthSurveys)
    },

    beforeDestroy: function () {
        bus.$off('health-survey-paused')
        bus.$off('health-survey-resumed')
    },

    methods: {
        ...mapMutations({
            setQueryCompany: 'adminCompanyHealthSurveys/setQuery',
            setQueriesCompany: 'adminCompanyHealthSurveys/setQueries',
            setQueryOrganization: 'adminOrganizationHealthSurveys/setQuery',
            setQueriesOrganization: 'adminOrganizationHealthSurveys/setQueries',
        }),

        setQuery: function (data) {
            if (this.isCompany) {
                this.setQueryCompany(data)
            }
            else if (this.isOrganization) {
                this.setQueryOrganization(data)
            }
        },

        setQueries: function (data) {
            if (this.isCompany) {
                this.setQueriesCompany(data)
            }
            else if (this.isOrganization) {
                this.setQueriesOrganization(data)
            }
        },

        /**
         * 検索条件の初期化
         */
        initQueryFromState: function () {
            if (localStorage.getItem('yokumiruWeb')) {
                const data = JSON.parse(localStorage.getItem('yokumiruWeb'))
                if (this.isCompany && data.adminCompanyHealthSurveys) {
                    this.setQueries(data.adminCompanyHealthSurveys)
                }
                else if (this.isOrganization && data.adminOrganizationHealthSurveys) {
                    this.setQueries(data.adminOrganizationHealthSurveys)
                }
            }
        },

        /**
         * 検索条件の初期化
         */
        initSearchCondition: function () {
            this.search.survey_start_date_gte = this.morphState.survey_start_date_gte
            this.search.survey_start_date_lte = this.morphState.survey_start_date_lte
            this.search.survey_finish_date_gte = this.morphState.survey_finish_date_gte
            this.search.survey_finish_date_lte = this.morphState.survey_finish_date_lte
            this.search.status = this.morphState.status
        },

        onChangeSearchSurveyStartDateGte: async function () {
            await this.setQuery({ key: 'survey_start_date_gte', value: this.search.survey_start_date_gte })
            await this.fetchHealthSurveys(1)
        },

        onChangeSearchSurveyStartDateLte: async function () {
            await this.setQuery({ key: 'survey_start_date_lte', value: this.search.survey_start_date_lte })
            await this.fetchHealthSurveys(1)
        },

        onChangeSearchSurveyFinishDateGte: async function () {
            await this.setQuery({ key: 'survey_finish_date_gte', value: this.search.survey_finish_date_gte })
            await this.fetchHealthSurveys(1)
        },

        onChangeSearchSurveyFinishDateLte: async function () {
            await this.setQuery({ key: 'survey_finish_date_lte', value: this.search.survey_finish_date_lte })
            await this.fetchHealthSurveys(1)
        },

        onChangeSearchStatus: async function () {
            await this.setQuery({ key: 'status', value: this.search.status })
            await this.fetchHealthSurveys(1)
        },

        onChangeSearchHealthSurveyTemplateId: async function () {
            await this.setQuery({ key: 'health_survey_template_id', value: this.search.health_survey_template_id })
            await this.fetchHealthSurveys(1)
        },

        /**
         * メンタルチェック質問デッキ一覧取得
         */
        fetchHealthSurveyTemplates: function () {
            axios.get(constants.admin.health_survey_templates.endpoint.base)
                .then(({data}) => {
                    this.healthSurveyTemplates = data.data
                })
        },

        /**
         * メンタルチェック回答一覧取得
         *
         * @param {number|object} param
         */
        fetchHealthSurveys: async function (param = null) {
            this.healthSurveys = null

            if (Number.isInteger(param) && param > 0) {
                await this.setQuery({ key: 'current_page', value: param })
            }
            else if (param !== null && param.hasOwnProperty('sortBy')) {
                await this.setQuery({ key: 'sortBy', value: param.sortBy })
                await this.setQuery({ key: 'sortDesc', value: param.sortDesc })
            }

            const endpoint = constants.admin.groups.endpoint.base

            axios.get(`${endpoint}/${this.group.id}/health_surveys${this.buildQuery()}`)
                .then(({data}) => {
                    this.healthSurveys = data.data
                    this.setQueries(data.meta)
                })
        },

        /**
         * QueryString生成
         *
         * @param {boolean} paginate
         * @return {string}
         */
        buildQuery: function (paginate = true) {
            let query = '?'

            const state = this.morphState

            if (paginate) {
                query += `page=${state.current_page}`
                query += `&sort=${state.sortBy}`
                query += `&desc=${state.sortDesc}`
            }
            else {
                query += `sort=${state.sortBy}`
                query += `&desc=${state.sortDesc}`
            }

            if (state.survey_start_date_gte) {
                query += `&survey_start_date_gte=${state.survey_start_date_gte}`
            }
            if (state.survey_start_date_lte) {
                query += `&survey_start_date_lte=${state.survey_start_date_lte}`
            }
            if (state.survey_finish_date_gte) {
                query += `&survey_finish_date_gte=${state.survey_finish_date_gte}`
            }
            if (state.survey_finish_date_lte) {
                query += `&survey_finish_date_lte=${state.survey_finish_date_lte}`
            }
            if (state.status) {
                query += `&status=${state.status}`
            }
            if (state.health_survey_template_id) {
                query += `&health_survey_template_id=${state.health_survey_template_id}`
            }

            return query
        },

        /**
         * QueryString生成（CSV出力用）
         * @return {string}
         */
        buildExportQuery: function () {
            let query = '?'

            const state = this.morphState

            if (state.survey_start_date_gte) {
                query += `&survey_start_date_gte=${state.survey_start_date_gte}`
            }
            if (state.survey_finish_date_lte) {
                query += `&survey_finish_date_lte=${state.survey_finish_date_lte}`
            }
            if (state.health_survey_template_id) {
                query += `&health_survey_template_id=${state.health_survey_template_id}`
            }

            return query
        },

        /**
         * 「データをダウンロード」ボタン押下時
         * @param {object} healthSurvey
         */
        onClickDownloadDaily: async function (healthSurvey) {
            if (this.inProgress) return
            this.inProgress = true

            const endpoint = `${constants.admin.groups.endpoint.base}/${this.group.id}/health_surveys/export`
            const query = `?survey_start_date=${healthSurvey.survey_start_date}&health_survey_template_id=${healthSurvey.health_survey_template_id}`
            const options = { responseType : 'blob' }

            await axios.get(`${endpoint}${query}`, options)
                .then((response) => {
                    this.errors = {}
                    const blob = new Blob([response.data], {
                        type: response.data.type,
                    })
                    saveAs(blob, `${this.group.name}_メンタルチェック回答一覧_${healthSurvey.survey_start_date.replaceAll('-', '')}.csv`)
                })
                .catch(() => {
                    bus.$emit('alert-danger', 'システムエラーが発生しました。')
                })

            this.inProgress = false
        },

        /**
         * 「選択期間中の集計データをダウンロード」ボタン押下時
         */
        onClickDownloadTerm: async function () {
            if (this.inProgress) return
            this.inProgress = true

            const endpoint = `${constants.admin.groups.endpoint.base}/${this.group.id}/health_surveys/export${this.buildExportQuery()}`
            const options = { responseType : 'blob' }

            await axios.get(endpoint, options)
                .then((response) => {
                    this.errors = {}
                    const blob = new Blob([response.data], {
                        type: response.data.type,
                    })
                    saveAs(blob, `${this.group.name}_メンタルチェック回答一覧_${this.search.survey_start_date_gte.replaceAll('-', '')}-${this.search.survey_finish_date_lte.replaceAll('-', '')}.csv`)
                })
                .catch(({response}) => {
                    if (response && response.hasOwnProperty('status') && response.status === 422) {
                        const alertText = this.healthSurveys.length > 0 ?  '集計期間と質問デッキを指定してください。' : '集計データが1件もありません。'
                        bus.$emit('alert-danger', alertText)
                        this.errors = {
                            survey_start_date_gte: [''],
                            survey_finish_date_lte: [''],
                            health_survey_template_id: [''],
                        }
                    }
                    else {
                        bus.$emit('alert-danger', 'システムエラーが発生しました。')
                    }
                })

            this.inProgress = false
        },

        /**
         * 一時停止ボタン押下時
         * @param {object} healthSurvey
         */
        onClickPause: function (healthSurvey) {
            bus.$emit('open-modal-admin-health-survey-pause', this.group, healthSurvey)
        },

        /**
         * 利用再開ボタン押下時
         * @param {object} healthSurvey
         */
        onClickResume: function (healthSurvey) {
            bus.$emit('open-modal-admin-health-survey-resume', this.group, healthSurvey)
        },
    },
}
</script>

<style lang="scss">
.adminGroupHealthSurveysWrapper {
    .b-table {
        th, td {
            font-size: 0.75rem;
        }
    }
}
</style>
